/**
 * Email mail-merge helpers for {{name}} placeholders (max 4 unique per template).
 * Names are user-chosen and saved to API param1–param4; Excel columns must match.
 */

/** Max Excel-backed merge fields per template. */
export const MAX_EMAIL_MERGE_PARAMETERS = 4;

/** Legacy auto-slot names (AI templates may still emit these). */
export const FIXED_EMAIL_MERGE_PARAMETERS = Array.from(
    { length: MAX_EMAIL_MERGE_PARAMETERS },
    (_, index) => `parameter${index + 1}`
);

const VARIABLE_REGEX = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
const VALID_MERGE_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const isValidEmailMergeVariableName = (name) =>
    Boolean(name && VALID_MERGE_NAME_PATTERN.test(name));

const PREVIEW_PALETTE = [
    {
        background: "#e3f2fd",
        border: "#90caf9",
        color: "#1565c0",
    },
    {
        background: "#e8f5e9",
        border: "#a5d6a7",
        color: "#2e7d32",
    },
    {
        background: "#fff8e1",
        border: "#ffe082",
        color: "#f9a825",
    },
    {
        background: "#f3e5f5",
        border: "#ce93d8",
        color: "#7b1fa2",
    },
    {
        background: "#eceff1",
        border: "#b0bec5",
        color: "#455a64",
    },
];

const previewStyleForName = (name, orderedNames) => {
    const idx = orderedNames.indexOf(name);
    if (idx >= 0 && idx < PREVIEW_PALETTE.length - 1) {
        return PREVIEW_PALETTE[idx];
    }
    return PREVIEW_PALETTE[PREVIEW_PALETTE.length - 1];
};

/** CSS class on inline merge-tag widgets in the Simple Editor. */
export const EMAIL_MERGE_TAG_CLASS = "email-merge-tag";

/**
 * Build inline widget HTML for one merge tag (shown in Simple Editor only).
 * @param {string} name
 * @returns {string}
 */
export const buildEmailMergeTagWidgetHtml = (name) => {
    const invalid = !isValidEmailMergeVariableName(name);
    const className = invalid
        ? `${EMAIL_MERGE_TAG_CLASS} email-merge-tag--invalid`
        : EMAIL_MERGE_TAG_CLASS;

    return `<span class="${className}" contenteditable="false" data-merge="${name}"><span class="email-merge-tag__label">{{${name}}}</span><button type="button" class="email-merge-tag__remove" contenteditable="false" tabindex="-1" aria-label="Remove {{${name}}}">\u00d7</button></span>`;
};

/**
 * Remove orphaned widget fragments left by earlier broken serialization.
 * @param {string} html
 * @returns {string}
 */
export const cleanupCorruptedMergeTagFragments = (html) => {
    let source = String(html || "");
    source = source.replace(
        /<button\b[^>]*\bemail-merge-tag__remove\b[^>]*>[\s\S]*?<\/button>\s*<\/span>/gi,
        ""
    );
    source = source.replace(
        /<button\b[^>]*\bemail-merge-tag__remove\b[^>]*>[\s\S]*?<\/button>/gi,
        ""
    );
    source = source.replace(/">×/g, "");
    return source;
};

/**
 * Strip Simple Editor widgets back to plain {{name}} tags for save/API/preview.
 * Converts widgets first, then removes orphan UI fragments.
 * @param {string} html
 * @returns {string}
 */
export const serializeEmailMergeTagsFromEditor = (html) => {
    const source = String(html || "");

    if (typeof document === "undefined") {
        return source.replace(/">×/g, "");
    }

    const template = document.createElement("template");
    template.innerHTML = source;

    Array.from(
        template.content.querySelectorAll(`span.${EMAIL_MERGE_TAG_CLASS}[data-merge]`)
    ).forEach((element) => {
        const name = element.getAttribute("data-merge");
        if (!name) return;
        element.replaceWith(document.createTextNode(`{{${name}}}`));
    });

    Array.from(template.content.querySelectorAll("button.email-merge-tag__remove")).forEach(
        (button) => {
            button.remove();
        }
    );

    return template.innerHTML.replace(/">×/g, "");
};

/**
 * Strip editor widgets and recover plain email HTML after mode switches.
 * Unescapes accidental &lt;tag&gt; source if no real tags remain.
 * @param {string} html
 * @returns {string}
 */
export const normalizePlainEmailHtml = (html) => {
    let plain = cleanupCorruptedMergeTagFragments(
        serializeEmailMergeTagsFromEditor(html)
    );

    const hasRealTag = /<[a-zA-Z!/?]/.test(plain);
    const hasEscapedTag = /&lt;[a-zA-Z!/?]/.test(plain);
    if (!hasRealTag && hasEscapedTag) {
        plain = plain
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&");
    }

    return plain;
};

/**
 * Plain HTML safe for Live Preview / API (never includes editor widgets).
 * @param {string} html
 * @returns {string}
 */
export const getPlainEmailHtmlForPreview = (html) =>
    normalizePlainEmailHtml(html);


const buildPreviewMergeTagHtml = (name, orderedNames) => {
    const style = previewStyleForName(name, orderedNames);
    const safeName = String(name)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    return (
        `<span class="email-preview-merge-tag"` +
        ` data-merge="${safeName}"` +
        ` title="Merge field: {{${safeName}}} — filled from your Excel/CSV column"` +
        ` style="display:inline;background:${style.background};color:${style.color};` +
        `border:1px solid ${style.border};border-radius:4px;padding:1px 6px;` +
        `margin:0 1px;font-weight:600;font-family:Consolas,monospace;font-size:0.92em;` +
        `white-space:nowrap;box-decoration-break:clone;-webkit-box-decoration-break:clone;">` +
        `{{${safeName}}}` +
        `</span>`
    );
};

const decoratePreviewMergeTagsInText = (text, orderedNames) => {
    VARIABLE_REGEX.lastIndex = 0;
    return String(text || "").replace(VARIABLE_REGEX, (_, name) =>
        buildPreviewMergeTagHtml(name, orderedNames)
    );
};

/**
 * Highlight {{name}} merge tags in preview HTML without mutating attributes/styles.
 * Only visible text nodes are decorated so full email layouts stay intact.
 * @param {string} html
 * @returns {string}
 */
export const decorateEmailMergeTagsForPreview = (html) => {
    const plain = String(html || "");
    if (!plain.trim()) return "";

    const orderedNames = extractAllEmailMergeVariableNames(plain);

    if (typeof document === "undefined" || !/<[a-zA-Z!/?]/.test(plain)) {
        return decoratePreviewMergeTagsInText(plain, orderedNames);
    }

    const template = document.createElement("template");
    template.innerHTML = plain;

    const walker = document.createTreeWalker(
        template.content,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode(node) {
                const parentTag = node.parentElement?.tagName?.toLowerCase?.();
                if (
                    !node.nodeValue ||
                    !node.nodeValue.includes("{{") ||
                    ["script", "style", "textarea", "title"].includes(parentTag)
                ) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            },
        }
    );

    const textNodes = [];
    let currentNode = walker.nextNode();
    while (currentNode) {
        textNodes.push(currentNode);
        currentNode = walker.nextNode();
    }

    textNodes.forEach((node) => {
        const source = node.nodeValue || "";
        const decorated = decoratePreviewMergeTagsInText(source, orderedNames);
        if (decorated === source) return;

        const fragment = document.createRange().createContextualFragment(decorated);
        node.parentNode?.replaceChild(fragment, node);
    });

    return template.innerHTML;
};

/**
 * Full-page HTML for "Preview in new tab".
 * Preserves the template's own HTML/head/styles when provided.
 * @param {string} html
 * @returns {string}
 */
export const buildEmailPreviewDocumentHtml = (html) => {
    const source = String(html || "").trim();
    if (!source) return "";

    const highlighted = decorateEmailMergeTagsForPreview(source);
    if (/<html[\s>]/i.test(source) || /<body[\s>]/i.test(source)) {
        return highlighted;
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Email Preview</title>
<style>
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: 100%;
    background: #f2f2f2;
  }
  body {
    box-sizing: border-box;
    padding: 32px 16px 48px;
    font-family: Arial, Helvetica, sans-serif;
  }
  .email-preview-shell {
    box-sizing: border-box;
    width: 600px;
    max-width: 100%;
    margin: 0 auto;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.15);
  }
  .email-preview-content {
    box-sizing: border-box;
    width: 100%;
    padding: 20px 24px;
    word-break: break-word;
    white-space: normal;
  }
  .email-preview-content img,
  .email-preview-content table,
  .email-preview-content iframe,
  .email-preview-content video {
    max-width: 100% !important;
    height: auto;
  }
</style>
</head>
<body>
  <div class="email-preview-shell">
    <div class="email-preview-content">${highlighted}</div>
  </div>
</body>
</html>`;
};

/**
 * Wrap each {{token}} in an inline removable widget for the Simple Editor.
 * @param {string} html
 * @returns {string}
 */
export const decorateEmailMergeTagsInHtml = (html) => {
    const plain = normalizePlainEmailHtml(html);
    return plain.replace(VARIABLE_REGEX, (_, name) => buildEmailMergeTagWidgetHtml(name));
};

/**
 * Escape raw HTML so tags render as editable text in a contentEditable source view.
 * @param {string} text
 * @returns {string}
 */
export const escapeHtmlForRawSourceEditor = (text) =>
    String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

/**
 * Decorate Raw HTML source for editing: show markup as text, {{parameterN}} as × widgets.
 * @param {string} rawHtml
 * @returns {string}
 */
export const decorateRawHtmlSourceForEditor = (rawHtml) => {
    const plain = normalizePlainEmailHtml(rawHtml);
    const escaped = escapeHtmlForRawSourceEditor(plain);
    VARIABLE_REGEX.lastIndex = 0;
    return escaped.replace(VARIABLE_REGEX, (_, name) => buildEmailMergeTagWidgetHtml(name));
};

/**
 * Read Raw HTML source editor DOM back to plain HTML.
 * Prefers walking a live element (avoids innerHTML entity round-trips).
 * @param {string} editorInnerHtml
 * @param {HTMLElement|null} [editorEl]
 * @returns {string}
 */
export const serializeRawHtmlSourceFromEditor = (editorInnerHtml, editorEl = null) => {
    if (editorEl && typeof editorEl === "object" && editorEl.childNodes) {
        const walk = (node) => {
            let out = "";
            Array.from(node.childNodes).forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE) {
                    out += child.nodeValue || "";
                    return;
                }
                if (child.nodeType !== Node.ELEMENT_NODE) return;

                if (child.classList?.contains(EMAIL_MERGE_TAG_CLASS)) {
                    const name = child.getAttribute("data-merge");
                    out += name ? `{{${name}}}` : "";
                    return;
                }
                if (child.tagName === "BR") {
                    out += "\n";
                    return;
                }
                out += walk(child);
            });
            return out;
        };
        return walk(editorEl);
    }

    const source = String(editorInnerHtml || "");
    if (typeof document === "undefined") {
        return source
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&");
    }

    const root = document.createElement("div");
    root.innerHTML = source;

    Array.from(
        root.querySelectorAll(`span.${EMAIL_MERGE_TAG_CLASS}[data-merge]`)
    ).forEach((element) => {
        const name = element.getAttribute("data-merge");
        element.replaceWith(document.createTextNode(name ? `{{${name}}}` : ""));
    });

    Array.from(root.querySelectorAll("button.email-merge-tag__remove")).forEach((button) => {
        button.remove();
    });

    Array.from(root.querySelectorAll("br")).forEach((br) => {
        br.replaceWith(document.createTextNode("\n"));
    });

    return root.textContent || "";
};

/** Selected-state class when a merge-tag chip is click-selected. */
export const EMAIL_MERGE_TAG_SELECTED_CLASS = "email-merge-tag--selected";

/** Inline format tags used when decorating a selected merge chip. */
const MERGE_TAG_FORMAT_COMMANDS = {
    bold: "strong",
    italic: "em",
    underline: "u",
    strikeThrough: "s",
};

/** MUI `sx` rules for merge-tag widgets inside contentEditable editors. */
export const EMAIL_MERGE_TAG_EDITOR_SX = {
    "& .email-merge-tag": {
        position: "relative",
        display: "inline-block",
        verticalAlign: "baseline",
        backgroundColor: "#e3f2fd",
        border: "1px solid #90caf9",
        borderRadius: "4px",
        padding: "0 4px",
        margin: "0 1px",
        lineHeight: 1.4,
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
    },
    "& .email-merge-tag--invalid": {
        backgroundColor: "#ffebee",
        borderColor: "#ef9a9a",
    },
    [`& .${EMAIL_MERGE_TAG_SELECTED_CLASS}`]: {
        backgroundColor: "#bbdefb",
        borderColor: "#1976d2",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.35)",
    },
    "& .email-merge-tag__label": {
        fontFamily: "Consolas, monospace",
        fontSize: "0.82em",
        pointerEvents: "none",
    },
    "& .email-merge-tag__remove": {
        position: "absolute",
        top: -9,
        right: -7,
        width: 14,
        height: 14,
        padding: 0,
        border: "none",
        borderRadius: "50%",
        backgroundColor: "#d32f2f",
        color: "#fff",
        fontSize: "11px",
        lineHeight: "14px",
        textAlign: "center",
        cursor: "pointer",
        userSelect: "none",
    },
    "& .email-merge-tag__remove:hover": {
        backgroundColor: "#b71c1c",
    },
};

/**
 * Clear selected styling from all merge-tag chips in an editor.
 * @param {HTMLElement|null} editorEl
 */
export const clearEmailMergeTagSelection = (editorEl) => {
    if (!editorEl) return;
    Array.from(
        editorEl.querySelectorAll(`.${EMAIL_MERGE_TAG_CLASS}.${EMAIL_MERGE_TAG_SELECTED_CLASS}`)
    ).forEach((el) => el.classList.remove(EMAIL_MERGE_TAG_SELECTED_CLASS));
};

/**
 * Select a merge-tag chip (same interaction model as clicking × to remove).
 * @param {HTMLElement} tagEl
 * @param {HTMLElement|null} editorEl
 * @returns {boolean}
 */
export const selectEmailMergeTagElement = (tagEl, editorEl) => {
    if (!tagEl || !editorEl?.contains(tagEl)) return false;

    clearEmailMergeTagSelection(editorEl);
    tagEl.classList.add(EMAIL_MERGE_TAG_SELECTED_CLASS);

    editorEl.focus();
    const selection = window.getSelection();
    if (!selection) return false;

    const range = document.createRange();
    range.selectNode(tagEl);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
};

/**
 * Resolve the currently selected merge-tag chip, if any.
 * @param {HTMLElement|null} editorEl
 * @returns {HTMLElement|null}
 */
export const getSelectedEmailMergeTagElement = (editorEl) => {
    if (!editorEl) return null;

    const marked = editorEl.querySelector(
        `.${EMAIL_MERGE_TAG_CLASS}.${EMAIL_MERGE_TAG_SELECTED_CLASS}`
    );
    if (marked) return marked;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const node = range.commonAncestorContainer;
    const el =
        node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    const tag = el?.closest?.(`.${EMAIL_MERGE_TAG_CLASS}`);
    if (tag && editorEl.contains(tag)) return tag;

    // Full chip selected via selectNode
    if (
        range.startContainer === range.endContainer &&
        range.startContainer.nodeType === Node.ELEMENT_NODE
    ) {
        const child = range.startContainer.childNodes[range.startOffset];
        if (child?.classList?.contains?.(EMAIL_MERGE_TAG_CLASS)) {
            return child;
        }
    }

    return null;
};

/**
 * Plain {{parameterN}} text for the selected merge chip (copy/cut).
 * @param {HTMLElement|null} editorEl
 * @returns {string|null}
 */
export const getSelectedEmailMergeTagPlainText = (editorEl) => {
    const tag = getSelectedEmailMergeTagElement(editorEl);
    const name = tag?.getAttribute("data-merge");
    return name ? `{{${name}}}` : null;
};

/**
 * Apply bold/italic/underline/strike around a selected merge chip, or toggle off.
 * @param {HTMLElement} tagEl
 * @param {string} command
 * @returns {boolean}
 */
export const formatSelectedEmailMergeTag = (tagEl, command) => {
    const wrapTag = MERGE_TAG_FORMAT_COMMANDS[command];
    if (!tagEl || !wrapTag) return false;

    const parent = tagEl.parentElement;
    if (
        parent &&
        parent.tagName.toLowerCase() === wrapTag &&
        parent.childNodes.length === 1
    ) {
        parent.replaceWith(tagEl);
        return true;
    }

    const wrapper = document.createElement(wrapTag);
    tagEl.parentNode?.insertBefore(wrapper, tagEl);
    wrapper.appendChild(tagEl);
    return true;
};

/**
 * Collect every unique {{token}} name from texts (any name, any count of uses).
 * Repeated {{parameter1}} still appears once in the result.
 * @param {...string} texts
 * @returns {string[]}
 */
export const extractAllEmailMergeVariableNames = (...texts) => {
    const seen = new Set();
    const ordered = [];

    texts.forEach((text) => {
        const source = String(text || "");
        let match;
        VARIABLE_REGEX.lastIndex = 0;
        while ((match = VARIABLE_REGEX.exec(source)) !== null) {
            const name = match[1];
            if (!seen.has(name)) {
                seen.add(name);
                ordered.push(name);
            }
        }
    });

    return ordered;
};

/**
 * Extract unique merge-parameter names in order of first use (max 4 enforced at validation).
 * @param {...string} texts
 * @returns {string[]}
 */
export const extractEmailMergeVariables = (...texts) =>
    extractAllEmailMergeVariableNames(...texts);

/**
 * @deprecated Use extractEmailMergeVariables — kept for callers expecting unsupported lists.
 * @param {...string} texts
 * @returns {string[]}
 */
export const getUnsupportedEmailMergeVariables = (...texts) => {
    const names = extractAllEmailMergeVariableNames(...texts);
    if (names.length <= MAX_EMAIL_MERGE_PARAMETERS) {
        return [];
    }
    return names.slice(MAX_EMAIL_MERGE_PARAMETERS);
};

/**
 * Count of unique merge parameters currently used in the email (0–4).
 * @param {...string} texts
 * @returns {number}
 */
export const getEmailMergeParameterCount = (...texts) =>
    extractEmailMergeVariables(...texts).length;

/**
 * Validate merge tags: at most 4 unique names; each must be a valid token.
 * @param {...string} texts
 * @returns {string|null} error message, or null when valid
 */
export const validateEmailMergeParameters = (...texts) => {
    const names = extractAllEmailMergeVariableNames(...texts);
    if (names.length > MAX_EMAIL_MERGE_PARAMETERS) {
        const extra = names
            .slice(MAX_EMAIL_MERGE_PARAMETERS)
            .map((name) => `{{${name}}}`)
            .join(", ");
        return `Maximum ${MAX_EMAIL_MERGE_PARAMETERS} unique parameters allowed. Remove: ${extra}`;
    }

    const invalid = names.filter((name) => !isValidEmailMergeVariableName(name));
    if (invalid.length > 0) {
        const tags = invalid.map((name) => `{{${name}}}`).join(", ");
        return `Invalid parameter name(s): ${tags}. Use letters, numbers, and underscore.`;
    }

    return null;
};

/**
 * Next legacy fixed slot (parameter1 → parameter4) not yet used.
 * @param {string[]} usedNames
 * @returns {string|null}
 */
export const getNextFixedEmailMergeParameter = (usedNames = []) => {
    const used = new Set(usedNames);
    return FIXED_EMAIL_MERGE_PARAMETERS.find((name) => !used.has(name)) || null;
};

/**
 * Remove every {{name}} occurrence from HTML/text (Raw HTML / AI / plain).
 * Leaves surrounding markup and copy intact.
 * @param {string} html
 * @param {string} name
 * @returns {string}
 */
export const removeEmailMergeParameterFromText = (html, name) => {
    if (!name) return String(html || "");
    const escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, "g");
    return String(html || "").replace(re, "");
};

/**
 * Rename every {{oldName}} to {{newName}} in HTML/text.
 * @param {string} html
 * @param {string} oldName
 * @param {string} newName
 * @returns {string}
 */
export const renameEmailMergeParameterInText = (html, oldName, newName) => {
    if (!oldName || !newName || oldName === newName) {
        return String(html || "");
    }
    const escaped = String(oldName).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, "g");
    return String(html || "").replace(re, `{{${newName}}}`);
};

/**
 * Format names as {{name}} tags.
 * @param {string[]} names
 * @returns {string[]}
 */
export const formatEmailMergeTags = (names = []) =>
    names.map((name) => `{{${name}}}`);

/**
 * Required Excel columns for a campaign (emails + parameter columns).
 * @param {string[]} variableNames
 * @returns {string[]}
 */
export const getRequiredExcelColumns = (variableNames = []) => [
    "emails",
    ...variableNames,
];

/**
 * Build API body fields param1–param4 from template content.
 * Maps unique merge tags in first-seen order to param1…param4; unused slots are null.
 * @param {...string} texts
 * @returns {{ param1: string|null, param2: string|null, param3: string|null, param4: string|null }}
 */
export const buildTemplateApiParamFields = (...texts) => {
    const names = extractEmailMergeVariables(...texts);
    return {
        param1: names[0] ?? null,
        param2: names[1] ?? null,
        param3: names[2] ?? null,
        param4: names[3] ?? null,
    };
};

/**
 * Normalize a user-entered variable name to a safe token.
 * @param {string} raw
 * @returns {string|null}
 */
export const normalizeEmailMergeVariableName = (raw) => {
    const cleaned = String(raw || "")
        .trim()
        .replace(/^\{\{|\}\}$/g, "")
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "");

    if (!cleaned || !/^[a-zA-Z_]/.test(cleaned)) {
        return null;
    }
    return cleaned;
};

/**
 * Validate a user-entered parameter name for insert/rename dialogs.
 * @param {string} raw
 * @param {string[]} usedNames
 * @param {{ excludeName?: string|null }} [options]
 * @returns {string|null} error message, or null when valid
 */
export const validateEmailMergeVariableNameInput = (
    raw,
    usedNames = [],
    { excludeName = null } = {}
) => {
    const normalized = normalizeEmailMergeVariableName(raw);
    if (!normalized) {
        return "Enter a valid name (letters, numbers, underscore; start with a letter or _).";
    }

    const used = new Set(
        usedNames.filter((name) => name && name !== excludeName)
    );
    if (used.has(normalized)) {
        return `Parameter "{{${normalized}}}" is already in use.`;
    }

    return null;
};

/**
 * Build notice lines for the Excel upload step.
 * @param {string[]} variableNames
 * @returns {string[]}
 */
export const buildExcelVariableNoticePoints = (variableNames = []) => {
    const columns = getRequiredExcelColumns(variableNames);
    const points = [
        "File size should not exceed 2mb.",
        "File format should be '.xlsx or .xls'.",
        `Excel must contain these columns: ${columns.join(", ")}.`,
    ];

    if (variableNames.length > 0) {
        points.push(
            `Template uses ${variableNames.length}/${MAX_EMAIL_MERGE_PARAMETERS} parameters (${formatEmailMergeTags(
                variableNames
            ).join(", ")}). Matching Excel columns fill each recipient.`
        );
    } else {
        points.push("Csv file should contain one 'emails' column.");
    }

    return points;
};

/**
 * Snapshot the caret in a contentEditable so it can be restored after a dialog/prompt.
 * @param {HTMLElement|null} editorEl
 * @returns {{ type: 'range', range: Range } | null}
 */
export const captureContentEditableCaret = (editorEl) => {
    if (!editorEl) return null;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    if (!editorEl.contains(range.commonAncestorContainer)) return null;

    return { type: "range", range: range.cloneRange() };
};

/**
 * Snapshot the caret in a textarea/input.
 * @param {HTMLTextAreaElement|HTMLInputElement|null} inputEl
 * @returns {{ type: 'text', start: number, end: number } | null}
 */
export const captureTextInputCaret = (inputEl) => {
    if (!inputEl) return null;
    return {
        type: "text",
        start: inputEl.selectionStart ?? inputEl.value.length,
        end: inputEl.selectionEnd ?? inputEl.value.length,
    };
};

/**
 * Insert text at a saved contentEditable caret (or at the end if missing).
 * @param {HTMLElement} editorEl
 * @param {string} text
 * @param {{ type: 'range', range: Range } | null} caret
 * @returns {string} updated innerHTML
 */
export const insertTextAtContentEditableCaret = (editorEl, text, caret) => {
    if (!editorEl) return "";

    editorEl.focus();
    const selection = window.getSelection();
    let range = null;

    if (caret?.type === "range" && caret.range) {
        try {
            range = caret.range;
            selection.removeAllRanges();
            selection.addRange(range);
        } catch (e) {
            range = null;
        }
    }

    if (!range) {
        range = document.createRange();
        range.selectNodeContents(editorEl);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    return editorEl.innerHTML;
};

/**
 * Plain-text character offset before a saved contentEditable caret.
 * @param {HTMLElement|null} editorEl
 * @param {{ type: 'range', range: Range } | null} caret
 * @param {Function} serializeHtml
 * @returns {number}
 */
export const getPlainTextOffsetBeforeCaret = (editorEl, caret, serializeHtml) => {
    if (!editorEl) return 0;

    const fullPlain = serializeHtml(editorEl.innerHTML || "", editorEl);

    if (caret?.type === "range" && caret.range) {
        try {
            const range = caret.range.cloneRange();
            if (!editorEl.contains(range.commonAncestorContainer)) {
                return fullPlain.length;
            }

            const pre = document.createRange();
            pre.selectNodeContents(editorEl);
            pre.setEnd(range.startContainer, range.startOffset);

            const holder = document.createElement("div");
            holder.appendChild(pre.cloneContents());
            return serializeHtml(holder.innerHTML || "", holder).length;
        } catch (e) {
            return fullPlain.length;
        }
    }

    return fullPlain.length;
};

/**
 * Place the contentEditable caret at a plain-text character offset.
 * Merge-tag widgets count as {{name}} in the offset math.
 * @param {HTMLElement|null} editorEl
 * @param {number} targetOffset
 * @returns {boolean}
 */
export const placeCaretAtPlainTextOffset = (editorEl, targetOffset) => {
    if (!editorEl || typeof document === "undefined") return false;

    let passed = 0;
    let placed = false;

    const applyRange = (range) => {
        const selection = window.getSelection();
        if (!selection) return false;
        selection.removeAllRanges();
        selection.addRange(range);
        editorEl.focus();
        return true;
    };

    const walk = (node) => {
        if (placed) return true;

        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.nodeValue || "";
            const len = text.length;
            if (passed + len >= targetOffset) {
                const range = document.createRange();
                range.setStart(node, Math.max(0, targetOffset - passed));
                range.collapse(true);
                placed = applyRange(range);
                return true;
            }
            passed += len;
            return false;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return false;

        if (node.classList?.contains(EMAIL_MERGE_TAG_CLASS)) {
            const mergeName = node.getAttribute("data-merge") || "";
            const len = `{{${mergeName}}}`.length;
            if (passed + len >= targetOffset) {
                if (!node.isConnected || !node.parentNode) return false;
                const range = document.createRange();
                range.setStartAfter(node);
                range.collapse(true);
                placed = applyRange(range);
                return true;
            }
            passed += len;
            return false;
        }

        if (node.tagName === "BR") {
            if (passed + 1 >= targetOffset) {
                const range = document.createRange();
                range.setStartAfter(node);
                range.collapse(true);
                placed = applyRange(range);
                return true;
            }
            passed += 1;
            return false;
        }

        for (const child of Array.from(node.childNodes)) {
            if (walk(child)) return true;
        }
        return false;
    };

    for (const child of Array.from(editorEl.childNodes)) {
        if (walk(child)) return placed;
    }

    if (!placed) {
        const range = document.createRange();
        range.selectNodeContents(editorEl);
        range.collapse(false);
        return applyRange(range);
    }

    return placed;
};

/**
 * Move the contentEditable caret immediately after a merge-tag widget.
 * @param {HTMLElement|null} editorEl
 * @param {string} name
 * @param {{ preferLast?: boolean }} [options]
 * @returns {boolean}
 */
export const placeCaretAfterMergeTag = (
    editorEl,
    name,
    { preferLast = true } = {}
) => {
    if (!editorEl || !name || typeof document === "undefined") return false;

    const findTag = () => {
        const safeName =
            typeof CSS !== "undefined" && CSS.escape
                ? CSS.escape(name)
                : String(name).replace(/"/g, '\\"');
        const tags = Array.from(
            editorEl.querySelectorAll(
                `span.${EMAIL_MERGE_TAG_CLASS}[data-merge="${safeName}"]`
            )
        ).filter((node) => node.isConnected && node.parentNode);
        if (tags.length === 0) return null;
        return preferLast ? tags[tags.length - 1] : tags[0];
    };

    const tag = findTag();
    if (!tag) return false;

    try {
        clearEmailMergeTagSelection(editorEl);
        const range = document.createRange();
        range.setStartAfter(tag);
        range.collapse(true);
        const selection = window.getSelection();
        if (!selection) return false;
        selection.removeAllRanges();
        selection.addRange(range);
        editorEl.focus();
        return true;
    } catch (e) {
        const retryTag = findTag();
        if (!retryTag) return false;
        try {
            const range = document.createRange();
            range.setStartAfter(retryTag);
            range.collapse(true);
            const selection = window.getSelection();
            if (!selection) return false;
            selection.removeAllRanges();
            selection.addRange(range);
            editorEl.focus();
            return true;
        } catch (err) {
            return false;
        }
    }
};

/**
 * Insert text into a string/textarea value at a saved caret.
 * @param {string} value
 * @param {string} text
 * @param {{ type: 'text', start: number, end: number } | null} caret
 * @returns {{ value: string, cursor: number }}
 */
export const insertTextAtStringCaret = (value, text, caret) => {
    const source = String(value || "");
    const start = caret?.type === "text" ? caret.start : source.length;
    const end = caret?.type === "text" ? caret.end : source.length;
    const next = `${source.slice(0, start)}${text}${source.slice(end)}`;
    return { value: next, cursor: start + text.length };
};









